/**
 * Read-only case details block for the Appeal Info modal left pane.
 */

import { CaseAppealsTableRowData } from "../../../../data-model/caseAppealsTable";
import { formatPatientName } from "../../../../pages/callingWorklist/table/utils/formatPatientName";
import {
  formatDobToDisplay,
  formatTimestampToDisplay,
} from "../../../../pages/pharmaOncologyAppeals/table/utils/formatDobToDisplay";
import DetailField from "../../nonEpaModal/components/detailField";

interface CaseDetailsContentProps {
  data: CaseAppealsTableRowData;
}

/** Placeholder shown when a case detail is missing on the appeal row. */
const EMPTY_VALUE = "--";

/**
 * Returns a trimmed field value, falling back to a placeholder when empty.
 *
 * @param value - Raw value from the appeal row
 * @returns Display-ready value
 */
const withFallback = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  return trimmed && trimmed !== EMPTY_VALUE ? trimmed : EMPTY_VALUE;
};

/**
 * Case Details tab content: patient identity, drug, response status,
 * delivery/appeal dates, and the appeal reason for the selected Case Appeals row.
 * Matches the Appeal Info Edit modal field set used by View and Edit.
 *
 * @param props.data - Appeal table row the modal was opened for
 * @returns Case details content element
 */
const CaseDetailsContent = (props: CaseDetailsContentProps) => {
  const { data } = props;
  const patientName = withFallback(formatPatientName(data?.patient_name ?? ""));
  const mrn = withFallback(data?.patient_mrn);
  const appealReason = withFallback(data?.appeal_reason);
  const dob = withFallback(formatDobToDisplay(data?.dob) || undefined);
  const fromDelivery = withFallback(
    formatTimestampToDisplay(data?.dumped_at) || undefined,
  );
  const toAppeal = withFallback(
    formatTimestampToDisplay(data?.appeal_sent_at) || undefined,
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="grid grid-cols-2 gap-4 border-b border-dashed border-primaryGray-14 pb-6">
        <DetailField
          label="Patient Name"
          value={
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate">{patientName}</span>
              {mrn !== EMPTY_VALUE && (
                <span className="rounded bg-primaryGray-16 px-2 py-0.5 text-tiny text-primaryGray-3">
                  {mrn}
                </span>
              )}
            </div>
          }
        />
        <DetailField label="Date of Birth" value={dob} />
      </div>

      <div className="grid grid-cols-2 gap-4 border-b border-dashed border-primaryGray-14 pb-6">
        <DetailField label="Drug" value={withFallback(data?.drug)} />
        <DetailField
          label="Response Status"
          value={
            <span className="font-bold">
              {withFallback(data?.response_status)}
            </span>
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4 border-b border-dashed border-primaryGray-14 pb-6">
        <DetailField label="From Delivery" value={fromDelivery} />
        <DetailField label="To Appeal" value={toAppeal} />
      </div>

      <div className="flex flex-col gap-2 rounded-md bg-primaryGray-16 p-4">
        <p className="m-0 text-tiny font-semibold text-primaryGray-9">
          Appeal Reason
        </p>
        <p className="m-0 whitespace-pre-wrap text-small leading-small text-primaryGray-1">
          {appealReason}
        </p>
      </div>
    </div>
  );
};

export default CaseDetailsContent;
